import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Loader2,
  MapPin,
  Ban,
  Gavel,
  Timer,
  Users,
  ShieldAlert,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  useAuctionDetail,
  usePlaceBid,
  useCancelAuction,
  bidErrorInfo,
  formatRemaining,
  secondsUntil,
} from "@/lib/auctions";

// Phase 22b — AuctionDetailModal.
// - 1.5 s polling on the detail so bid history + current price stay live.
// - Client-side countdown ticks every 1 s off the last-known `ends_at`.
// - Outbid detection: when the local user was previously highest and the
//   next server response reports a different `highest_bidder_id`, a
//   generic "You've been outbid!" toast fires (never reveals bidder name).
// - Anti-sniping extension: when `ends_at` moves forward AFTER a bid,
//   an "extended" toast fires.
// - Countdown urgency: red under 30 s, subtle pulse under 10 s. The pulse
//   is a CSS class (`.countdown-critical-pulse`) — Phase 17a ASL
//   motion-reduce sweeps it correctly (color stays; motion collapses).

export default function AuctionDetailModal({ id, onClose }) {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const detail = useAuctionDetail(id);
  const placeBid = usePlaceBid();
  const cancelAuction = useCancelAuction();
  const [photoIdx, setPhotoIdx] = useState(0);
  const [bidAmount, setBidAmount] = useState("");
  const [error, setError] = useState("");
  const wasHighestRef = useRef(false);
  const lastEndsAtRef = useRef(null);
  const auction = detail.data;
  const uid = user?.id;

  useEffect(() => {
    setPhotoIdx(0);
    setBidAmount("");
    setError("");
    wasHighestRef.current = false;
    lastEndsAtRef.current = null;
  }, [id]);

  // ── Outbid detection + anti-sniping extension toasts ───────────────
  useEffect(() => {
    if (!auction || !uid) return;

    // Outbid detection — was highest previously, now someone else is.
    const isHighest = auction.highest_bidder_id === uid;
    if (
      wasHighestRef.current &&
      !isHighest &&
      auction.highest_bidder_id &&
      auction.status === "active"
    ) {
      toast.error(
        t(
          "matchbid.outbid_toast",
          "You've been outbid! Current bid: {price} FT."
        ).replace("{price}", String(auction.current_price)),
        { id: `outbid-${auction.id}` }
      );
      // Refresh balance so the released escrow shows in the header immediately.
      if (typeof refresh === "function") {
        try {
          refresh();
        } catch (_) {
          /* ignore */
        }
      }
    }
    wasHighestRef.current = isHighest;

    // Anti-sniping extension — ends_at moved strictly forward.
    if (
      lastEndsAtRef.current &&
      auction.ends_at &&
      new Date(auction.ends_at).getTime() >
        new Date(lastEndsAtRef.current).getTime() + 500
    ) {
      toast.info(
        t(
          "matchbid.extended_toast",
          "Auction extended by 30s — someone bid at the buzzer."
        ),
        { id: `extended-${auction.id}-${auction.ends_at}` }
      );
    }
    lastEndsAtRef.current = auction.ends_at;
  }, [
    auction?.highest_bidder_id,
    auction?.ends_at,
    auction?.current_price,
    auction?.status,
    uid,
    refresh,
    t,
    auction,
  ]);

  // ── Live per-second countdown tick ─────────────────────────────────
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!auction?.ends_at) return;
    setTick(secondsUntil(auction.ends_at));
    const iv = setInterval(
      () => setTick(secondsUntil(auction.ends_at)),
      1000
    );
    return () => clearInterval(iv);
  }, [auction?.ends_at]);

  const isSeller = !!auction && auction.seller_id === uid;
  const isHighest = !!auction && auction.highest_bidder_id === uid;
  const isEnded = !!auction && auction.status !== "active";
  const isActive = !!auction && auction.status === "active" && tick > 0;

  // ── Derived bid constraints ────────────────────────────────────────
  const requiredNext = useMemo(() => {
    if (!auction) return 0;
    const min = Number(auction.min_increment || 5);
    const cur = Number(auction.current_price || 0);
    // If the caller is currently top bidder, they may raise from cur (bumping only).
    // Otherwise they must beat cur by min_increment.
    return isHighest ? cur + 1 : cur + min;
  }, [auction, isHighest]);

  const bidInt = parseInt(bidAmount, 10);
  const bidValid =
    Number.isFinite(bidInt) && bidInt >= requiredNext && bidInt <= 999_999;

  // Balance affordability check accounts for the escrow-delta trick — if
  // the caller is already the top bidder, they only need `bidInt - cur`
  // available. Server confirms the exact delta atomically anyway.
  const availableForBid = useMemo(() => {
    if (!user) return 0;
    return Number(user.figutokens_balance || 0);
  }, [user]);
  const deltaNeeded = isHighest
    ? Math.max(0, (bidInt || 0) - Number(auction?.current_price || 0))
    : bidInt || 0;
  const canAfford = availableForBid >= deltaNeeded;

  const onSubmitBid = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!auction || !bidValid) return;
      setError("");
      try {
        const resp = await placeBid.mutateAsync({
          id: auction.id,
          amount: bidInt,
        });
        setBidAmount("");
        toast.success(
          t("matchbid.bid_success", "Bid placed. You're on top for now.")
        );
        if (resp?.extended) {
          toast.info(
            t(
              "matchbid.extended_own_toast",
              "Your bid extended the auction by 30s."
            ),
            { id: `own-extended-${auction.id}-${resp.ends_at}` }
          );
        }
        if (typeof refresh === "function") {
          try {
            await refresh();
          } catch (_) {
            /* ignore */
          }
        }
      } catch (err) {
        const info = bidErrorInfo(err);
        let msg = info.message;
        if (info.code === "insufficient_balance") {
          msg = t(
            "matchbid.err_insufficient",
            "Not enough FiguTokens — top up via the Wallet."
          );
        } else if (info.code === "bid_too_low") {
          msg = t(
            "matchbid.err_bid_too_low",
            "Your bid is below the required minimum ({required} FT)."
          ).replace("{required}", String(info.required ?? requiredNext));
        } else if (info.code === "outbid_or_expired") {
          msg = t(
            "matchbid.err_outbid",
            "Someone bid faster or the auction just ended. Try again."
          );
        } else if (info.code === "cannot_bid_on_own_auction") {
          msg = t(
            "matchbid.err_own_auction",
            "You cannot bid on your own auction."
          );
        } else if (info.code === "auction_not_active") {
          msg = t(
            "matchbid.err_not_active",
            "This auction is no longer accepting bids."
          );
        }
        setError(msg);
      }
    },
    [auction, bidInt, bidValid, placeBid, refresh, requiredNext, t]
  );

  const onCancel = useCallback(async () => {
    if (!auction) return;
    setError("");
    try {
      await cancelAuction.mutateAsync(auction.id);
      toast.success(t("matchbid.cancel_success", "Auction cancelled."));
      onClose();
    } catch (err) {
      const info = bidErrorInfo(err);
      let msg = info.message;
      if (info.code === "cannot_cancel_with_bids") {
        msg = t(
          "matchbid.err_cannot_cancel_with_bids",
          "Auctions with bids must run to close."
        );
      }
      setError(msg);
    }
  }, [auction, cancelAuction, onClose, t]);

  // ── Countdown styling ──────────────────────────────────────────────
  const critical = tick <= 30;
  const urgent = tick <= 10;
  const countdownCx = useMemo(() => {
    const base =
      "font-display text-3xl font-bold tabular-nums flex items-center gap-2 transition-colors";
    if (!isActive) return `${base} text-[#A1A1AA]`;
    if (urgent) return `${base} text-[#FF3B3B] countdown-critical-pulse`;
    if (critical) return `${base} text-[#FF6B6B]`;
    return `${base} text-[#E5B83A]`;
  }, [isActive, urgent, critical]);

  // ── Status label ───────────────────────────────────────────────────
  const statusLabel = useMemo(() => {
    if (!auction) return "";
    switch (auction.status) {
      case "ended":
        return isHighest || auction.winner_id === uid
          ? t("matchbid.status_won", "You won this auction!")
          : t("matchbid.status_sold", "Auction ended.");
      case "reserve_not_met":
        return t(
          "matchbid.status_reserve_not_met",
          "Reserve price not met — no sale."
        );
      case "expired_no_bids":
        return t("matchbid.status_expired_no_bids", "Auction expired with no bids.");
      case "cancelled":
        return t("matchbid.status_cancelled", "Cancelled by seller.");
      default:
        return "";
    }
  }, [auction, isHighest, uid, t]);

  if (!id) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="auction-detail-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="auction-detail-modal"
        data-auction-status={auction?.status || "loading"}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] max-h-[92vh] bg-[#0A0A0A] border border-white/10 rounded-xl shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2
            data-testid="auction-detail-title"
            className="font-display text-lg font-semibold text-white flex items-center gap-2"
          >
            <Gavel className="h-4 w-4 text-[#E5B83A]" />
            {auction ? `#${auction.number}` : t("matchbid.loading", "Loading…")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="auction-detail-close"
            aria-label="Close"
            className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/5 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {detail.isLoading && !auction ? (
            <div className="flex items-center justify-center py-16 text-white/60">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !auction ? (
            <div className="p-8 text-center text-sm text-[#A1A1AA] font-body">
              {t("matchbid.not_found", "Auction not available.")}
            </div>
          ) : (
            <div className="px-5 py-5 space-y-5">
              {/* Photo carousel */}
              <div className="rounded-md overflow-hidden bg-black">
                {auction.photo_urls && auction.photo_urls.length > 0 ? (
                  <>
                    <img
                      src={auction.photo_urls[photoIdx] || auction.photo_urls[0]}
                      alt={`Sticker #${auction.number}`}
                      className="w-full max-h-[280px] object-contain bg-black"
                    />
                    {auction.photo_urls.length > 1 ? (
                      <div className="flex justify-center gap-2 py-2 bg-black/60">
                        {auction.photo_urls.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            aria-label={`Photo ${i + 1}`}
                            onClick={() => setPhotoIdx(i)}
                            className={
                              "h-2 w-2 rounded-full transition-all " +
                              (i === photoIdx
                                ? "bg-[#FFD700] w-4"
                                : "bg-white/30")
                            }
                          />
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center text-white/40 font-mono text-2xl">
                    #{auction.number}
                  </div>
                )}
              </div>

              {/* Countdown + price */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-body">
                    {auction.bid_count > 0
                      ? t("matchbid.current_bid", "current bid")
                      : t("matchbid.starting_at", "starting at")}
                  </p>
                  <p
                    className="font-display text-3xl font-semibold text-[#E5B83A]"
                    data-testid="auction-detail-price"
                  >
                    {auction.current_price} FT
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-body">
                    {t("matchbid.time_left", "time left")}
                  </p>
                  <p
                    className={countdownCx}
                    data-testid="auction-detail-countdown"
                    data-time-remaining-sec={tick}
                    data-critical={critical ? "true" : "false"}
                    data-urgent={urgent ? "true" : "false"}
                  >
                    <Timer className="h-5 w-5" />
                    {formatRemaining(tick)}
                  </p>
                </div>
              </div>

              {/* Meta strip */}
              <div className="flex items-center justify-between text-xs text-[#A1A1AA] font-body">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span data-testid="auction-detail-bid-count">
                    {auction.bid_count}
                  </span>{" "}
                  {t("matchbid.bids_short", "bids")}
                </span>
                <span>
                  {t("matchbid.grade_label", "Grade")}:{" "}
                  <span
                    className="text-[#FFD700]"
                    data-testid="auction-detail-grade"
                  >
                    {auction.grade}
                  </span>
                </span>
                {Number.isFinite(auction.distance_meters) ? (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {Math.round(auction.distance_meters)}m
                  </span>
                ) : null}
              </div>

              {/* Reserve status */}
              {auction.reserve_price ? (
                <div
                  data-testid="auction-detail-reserve"
                  className={
                    "flex items-center gap-2 text-xs font-body px-3 py-2 rounded-md border " +
                    (auction.current_price >= auction.reserve_price
                      ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
                      : "border-[#FFB86B]/40 bg-[#FFB86B]/5 text-[#FFB86B]")
                  }
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {auction.current_price >= auction.reserve_price
                    ? t("matchbid.reserve_met", "Reserve met — will sell.")
                    : t(
                        "matchbid.reserve_not_met_yet",
                        "Reserve not met yet (hidden amount)."
                      )}
                </div>
              ) : null}

              {/* Seller strip */}
              <div className="flex items-center justify-between text-xs text-[#A1A1AA] font-body">
                <span>
                  {t("matchbid.seller_label", "Seller")}:{" "}
                  <span
                    className="text-white"
                    data-testid="auction-detail-seller"
                  >
                    {auction.seller_display_name || "—"}
                  </span>
                </span>
                {isHighest && isActive ? (
                  <span
                    className="text-[#39FF14] font-medium flex items-center gap-1"
                    data-testid="auction-detail-you-are-highest"
                  >
                    <Trophy className="h-3 w-3" />
                    {t("matchbid.you_are_highest", "You're the top bidder.")}
                  </span>
                ) : null}
              </div>

              {auction.description ? (
                <p
                  className="text-sm text-white/80 font-body whitespace-pre-wrap"
                  data-testid="auction-detail-description"
                >
                  {auction.description}
                </p>
              ) : null}

              {/* Bid history */}
              <div>
                <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-body mb-2">
                  {t("matchbid.bid_history_title", "Recent bids")}
                </p>
                {(auction.bids || []).length === 0 ? (
                  <p
                    data-testid="auction-detail-no-bids"
                    className="text-xs text-[#A1A1AA] font-body italic"
                  >
                    {t("matchbid.no_bids_yet", "No bids yet — be the first!")}
                  </p>
                ) : (
                  <ul
                    data-testid="auction-detail-bid-history"
                    className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1"
                  >
                    {(auction.bids || []).map((b, idx) => (
                      <li
                        key={b.id || idx}
                        data-testid={`auction-bid-row-${idx}`}
                        className={
                          "flex items-center justify-between text-xs font-body px-3 py-2 rounded-md border " +
                          (idx === 0
                            ? "border-[#FFD700]/40 bg-[#FFD700]/5 text-white"
                            : "border-white/5 bg-black/40 text-[#A1A1AA]")
                        }
                      >
                        <span>
                          {b.is_own
                            ? t("matchbid.you_label", "You")
                            : b.bidder_display}
                        </span>
                        <span className="font-mono text-[#E5B83A]">
                          {b.amount} FT
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Status label (post-close) */}
              {isEnded ? (
                <div
                  data-testid="auction-detail-status-label"
                  className="rounded-md border border-[#FFB86B]/40 bg-[#FFB86B]/5 px-3 py-2 text-sm text-[#FFB86B] font-body flex items-center gap-2"
                >
                  <Trophy className="h-4 w-4" />
                  {statusLabel}
                </div>
              ) : null}

              {/* Error */}
              {error ? (
                <div
                  data-testid="auction-detail-error"
                  role="alert"
                  className="rounded-md border-2 border-[#FF0000] bg-[#3B0000]/40 px-3 py-2 text-sm text-[#FFAAAA] font-body"
                >
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer — bid form or seller cancel */}
        {auction && isActive && !isSeller ? (
          <footer className="px-5 py-4 border-t border-white/10 space-y-2">
            <form
              onSubmit={onSubmitBid}
              className="flex items-center gap-2"
              noValidate
            >
              <input
                type="number"
                min={requiredNext}
                max={999_999}
                step={1}
                inputMode="numeric"
                value={bidAmount}
                onChange={(e) =>
                  setBidAmount(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder={t(
                  "matchbid.bid_placeholder",
                  "min {min} FT"
                ).replace("{min}", String(requiredNext))}
                data-testid="auction-bid-input"
                className="flex-1 h-11 bg-[#121212] border border-white/10 rounded-md px-3 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]"
              />
              <button
                type="submit"
                disabled={!bidValid || !canAfford || placeBid.isPending}
                data-testid="auction-bid-submit"
                className="h-11 px-5 rounded-md font-semibold text-sm bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {placeBid.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Gavel className="h-4 w-4" />
                )}
                {t("matchbid.place_bid_button", "Place bid")}
              </button>
            </form>
            <p className="text-[11px] text-[#A1A1AA] font-body text-center">
              {t(
                "matchbid.min_next_bid",
                "Min next bid: {min} FT · Your balance: {bal} FT"
              )
                .replace("{min}", String(requiredNext))
                .replace("{bal}", String(availableForBid))}
            </p>
          </footer>
        ) : auction && isActive && isSeller ? (
          <footer className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelAuction.isPending || auction.bid_count > 0}
              title={
                auction.bid_count > 0
                  ? t(
                      "matchbid.err_cannot_cancel_with_bids",
                      "Auctions with bids must run to close."
                    )
                  : ""
              }
              data-testid="auction-detail-cancel"
              className="flex-1 h-11 rounded-md border border-[#FF6B6B]/50 text-[#FF6B6B] font-medium text-sm hover:bg-[#FF6B6B]/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {cancelAuction.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              {t("matchbid.cancel_auction", "Cancel auction")}
            </button>
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
